import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button, Grid, useTheme, Container } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import GavelIcon from '@mui/icons-material/Gavel';
// import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import StatWidget from '../components/widgets/stat.widget';
import HorizontalTabs from '../components/elements/horizontal-tabs.element';
import AuctionWidget from '../components/widgets/auction.widget';
import HorizontalSlider from '../components/elements/horizontal-slider.element';
import CardStatWidget from '../components/widgets/card-stat.widget';
import { auctionList, getItemList } from '../apis';
import {
  APIResponse,
  Auction,
  Item,
  ListingResponse,
  TypeSnackbarPayload,
} from '../utilities/types';
import { showSnackbar } from '../store/slices/common.slice';
import FormModal from '../components/modals/form.modal';
import AuctionForm from '../components/forms/auction.form';
import CustomSwal from '../components/elements/custom-swal.element';
import { RootState } from '../store';
import AuthFormModal from '../components/modals/auth-form.modal';
import useAuctionBroadcastWsHook from '../hooks/auction-broadcast-ws.hook';
import useNavigateHook from '../hooks/navigate.hook';
import ItemWidget from '../components/widgets/item.widget';

const HomePage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { doNavigate } = useNavigateHook();

  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [comingAuctions, setComingAuctions] = useState<Auction[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [showAuctionFormModal, setShowAuctionFormModal] = useState(false);
  const [showAuthFormModal, setShowAuthFormModal] = useState(false);

  // Simulate API call (replace with your real API)
  const loadAuctions = useCallback(async () => {
    try {
      const response = (await auctionList({
        listing_type: 'current',
      })) as APIResponse<ListingResponse>;
      if (response.status) {
        setAuctions(Array.isArray(response.data?.items) ? response.data.items : []);
      }
    } catch (err: any) {
      const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
      if (err?.data?.message) {
        snakbarpayload.message = err?.data?.message;
      } else if (err?.statusText) {
        snakbarpayload.message = err?.statusText;
      }
      dispatch(showSnackbar(snakbarpayload));
    }
  }, [dispatch]);

  const loadComingAuctions = useCallback(async () => {
    try {
      const response = (await auctionList({
        listing_type: 'future',
      })) as APIResponse<ListingResponse>;
      if (response.status) {
        setComingAuctions(Array.isArray(response.data?.items) ? response.data.items : []);
      }
    } catch (err: any) {
      const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
      if (err?.data?.message) {
        snakbarpayload.message = err?.data?.message;
      } else if (err?.statusText) {
        snakbarpayload.message = err?.statusText;
      }
      dispatch(showSnackbar(snakbarpayload));
    }
  }, [dispatch]);

  const loadItems = useCallback(async () => {
    try {
      const response = (await getItemList({})) as APIResponse<ListingResponse>;
      if (response.status) {
        setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      }
    } catch (err: any) {
      const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
      if (err?.data?.message) {
        snakbarpayload.message = err?.data?.message;
      } else if (err?.statusText) {
        snakbarpayload.message = err?.statusText;
      }
      dispatch(showSnackbar(snakbarpayload));
    }
  }, [dispatch]);

  const openAuctionCreateForm = () => {
    if (isAuthenticated) {
      setShowAuctionFormModal(true);
    } else {
      setShowAuthFormModal(true);
    }
  };

  const onAuctionCreate = () => {
    CustomSwal.fire({
      icon: 'success',
      title: 'Done',
      text: `Auction created successfully`,
    });
    dispatch(showSnackbar({ message: 'Auction created successfully', severity: 'success' }));
    setShowAuctionFormModal(false);
  };

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    loadAuctions();
    loadComingAuctions();
  }, [loadAuctions, loadComingAuctions]);

  useAuctionBroadcastWsHook(token, () => {
    loadAuctions();
    loadComingAuctions();
  });

  return (
    <>
      <Box
        sx={{
          bgcolor: '#f8fffa',
          textAlign: 'center',
          py: 8,
          px: 2,
        }}
      >
        {/* Title */}
        <Typography variant="h1" fontWeight="bold" gutterBottom>
          {`India's Premier`}
        </Typography>
        <Typography
          variant="h1"
          fontWeight="bold"
          gutterBottom
          sx={{ color: theme.palette.primary.main }}
        >
          Scrap Auction Platform
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: '700px', mx: 'auto', mb: 4 }}>
          Connect buyers and sellers in a transparent marketplace for steel, metal, plastic,
          electronics, and more. Get the best prices through competitive bidding.
        </Typography>

        {/* CTA Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 8 }}>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForward />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
            }}
            onClick={openAuctionCreateForm}
          >
            Sell Your Scrap
          </Button>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
            }}
            onClick={() => {
              doNavigate('auctions.html');
            }}
          >
            Browse Auctions
          </Button>
        </Box>

        {/* Stats Section */}
        <Grid container spacing={4} justifyContent="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <StatWidget
              icon={<TrendingUpIcon sx={{ fontSize: 40, color: 'green', mb: 1 }} />}
              label="Total Value Traded"
              value="₹50+ Crores"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <StatWidget
              icon={<ShieldOutlinedIcon sx={{ fontSize: 40, color: 'green', mb: 1 }} />}
              label="Verified Buyers & Sellers"
              value="5000+"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <StatWidget
              icon={<AccessTimeOutlinedIcon sx={{ fontSize: 40, color: 'green', mb: 1 }} />}
              label="Live Auction Support"
              value="24/7"
            />
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          bgcolor: '#fbf9fa',
          py: 8,
          px: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h2" gutterBottom>
            Live Market Activity
          </Typography>
          <Typography variant="body1" gutterBottom>
            {`See what's happening right now on our platform. Join thousands of traders participating in active auctions across India.`}
          </Typography>
        </Box>
        <Box sx={{ mt: 4, px: 4, mx: 'auto', maxWidth: '800px', textAlign: 'center' }}>
          <HorizontalTabs
            items={[
              {
                label: 'Coming Auctions',
                value: 0,
                icon: <GavelIcon fontSize="small" />,
                count: comingAuctions.length,
              },
              {
                label: 'Live Auctions',
                value: 1,
                icon: <GavelIcon fontSize="small" />,
                count: auctions.length,
              },
            ]}
            selectedTab={selectedTab}
            onChange={(newValue: string | number) => {
              setSelectedTab(newValue as number);
            }}
          />
          {/* <HorizontalTabs
          items={[
            {
              label: 'Auctions',
              value: 0,
              icon: <GavelIcon fontSize="small" />,
              count: auctions.length,
            },
            {
              label: 'Quotations',
              value: 1,
              icon: <DescriptionOutlinedIcon fontSize="small" />,
              count: 0,
            },
            {
              label: 'Reverse Auctions',
              value: 2,
              icon: <SwapVertOutlinedIcon fontSize="small" />,
              count: 0,
            },
          ]}
          selectedTab={selectedTab}
          onChange={(newValue: string | number) => {
            setSelectedTab(newValue as number);
          }}
        /> */}
        </Box>
        <Box sx={{ my: 8, px: { md: 4, xs: 1, lg: 5 } }}>
          {selectedTab === 0 && (
            <>
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h1" gutterBottom sx={{ fontWeight: '600' }}>
                  Coming Auctions ({comingAuctions.length})
                </Typography>
                <Typography variant="body1" gutterBottom color="textSecondary">
                  Auctions that will start soon. Keep updated
                </Typography>
              </Box>
              <Box sx={{ mt: 4 }}>
                <HorizontalSlider
                  items={comingAuctions.map((item: Auction, itemIndex) => (
                    <AuctionWidget
                      key={`coming-auction-list-${itemIndex}`}
                      auctionDetail={item}
                    />
                  ))}
                />
              </Box>
            </>
          )}
          {selectedTab === 1 && (
            <>
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h1" gutterBottom sx={{ fontWeight: '600' }}>
                  Live Auctions ({auctions.length})
                </Typography>
                <Typography variant="body1" gutterBottom color="textSecondary">
                  Sellers auction their scrap materials to the highest bidder
                </Typography>
              </Box>
              <Box sx={{ mt: 4 }}>
                <HorizontalSlider
                  items={auctions.map((item: Auction, itemIndex) => (
                    <AuctionWidget key={`auction-list-${itemIndex}`} auctionDetail={item} />
                  ))}
                />
              </Box>
            </>
          )}
          {selectedTab === 2 && (
            <>
              <Typography variant="h4" color="text.secondary.dark" textAlign="center">
                Reverse Auctions
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  mt: 4,
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 300,
                }}
              >
                <Typography component="span" variant="body2" color="text.secondary">
                  No reverse auctions found
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          py: 8,
          px: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h2" gutterBottom>
            How Scrap Auction Works
          </Typography>
          <Typography variant="body1" gutterBottom>
            {`Multiple auction formats to match your business needs. Whether you're buying or selling, we have the right solution for you.`}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 3,
            px: 2,
            py: 6,
          }}
        >
          <CardStatWidget
            icon={<ArrowUpwardIcon sx={{ fontSize: 40, color: 'blue' }} />}
            title="Forward Auction"
            description="Sellers list items and buyers bid higher prices. Perfect for rare or high-value scrap materials."
          />
          <CardStatWidget
            icon={<ArrowDownwardIcon sx={{ fontSize: 40, color: 'green' }} />}
            title="Reverse Auction"
            description="Buyers post requirements and sellers compete with lower prices. Ideal for bulk purchases."
          />
          <CardStatWidget
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 40, color: 'purple' }} />}
            title="Quotation System"
            description="Request quotes from multiple suppliers. Compare prices and choose the best offer."
          />
          <CardStatWidget
            icon={<ImportExportIcon sx={{ fontSize: 40, color: 'orange' }} />}
            title="Price & Rank Bidding"
            description="Advanced bidding systems with real-time price updates and ranking during auction time."
          />
        </Box>
      </Box>
      {items.length > 0 && (
        <Box
          sx={{
            bgcolor: '#fbf9fa',
            py: 8,
            px: 2,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" gutterBottom>
              Scrap Categories
            </Typography>
            <Typography variant="body1" gutterBottom>
              Browse through our wide range of scrap categories. Each category has active auctions
              with verified buyers and sellers.
            </Typography>
          </Box>
          <Container sx={{ py: 4 }}>
            <Grid container spacing={3}>
              {items.map((cat: Item, catIndex) => (
                <Grid key={`item-category-index-${catIndex}`} size={{ xs: 12, sm: 2, md: 4 }}>
                  <ItemWidget item={cat} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}
      {showAuctionFormModal && (
        <FormModal
          open={showAuctionFormModal}
          onClose={() => setShowAuctionFormModal(false)}
          size="sm"
        >
          <AuctionForm onSuccess={onAuctionCreate} context="seller" />
        </FormModal>
      )}
      {showAuthFormModal && (
        <AuthFormModal
          open={showAuthFormModal}
          onClose={() => setShowAuthFormModal(false)}
          onDone={() => setShowAuthFormModal(false)}
          size="xs"
        />
      )}
    </>
  );
};

export default HomePage;
